import { useNavigate } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import Button from "../../components/button/Button";
import Input, { InputValidation } from "../../components/input/Input";
import Modal, { ModalProps } from "../../components/modal/Modal";
import Select from "../../components/select/Select";
import ToolTip from "../../components/ToolTip/ToolTip";
import LayoutContext from "../../context/layout-context";
import { defaultCardSchemeOptions } from "../../utils/constants";
import { configureTokenRequestor } from "../../utils/helpers/data/card-tokenization";
import { TokenRequestorConfigData } from "../../utils/helpers/types";
import { PartialLoose } from "../../utils/helpers/types-helpers";
import { alphaNumericValidation } from "../../utils/helpers/validation-helpers";
import { resources } from "../../utils/resources";

const initialFormData: TokenRequestorConfigData = {
  issuerId: "",
  cardScheme: "",
  tokenRequestorId: ""
};

const validations: PartialLoose<TokenRequestorConfigData, InputValidation> = {
  issuerId: {
    maxLength: 10,
    required: true
  },
  tokenRequestorId: {
    maxLength: 11,
    required: true
  }
};

const ConfigureTokenRequestor: FunctionComponent = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { notify } = useContext(LayoutContext);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.cardScheme) {
      notify("error", "Please select a card scheme");
      return;
    }
    setLoading(true);
    const response = await configureTokenRequestor(formData);
    setLoading(false);
    if (response.error) {
      notify("error", response.message as string);
      return;
    }
    notify("success", "Configuration applied successfully");
    setFormData(initialFormData);
  };

  const handleChange = (key: keyof TokenRequestorConfigData, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  return (
    <main className="content-layout">
      <form onSubmit={handleSave} className="generic-form token-requestor-form">
        <div className="input-group">
          <span className="question">
            <span className="required">Issuer ID</span>
            <ToolTip
              toolTip="Issuer issuerId generated by the Interswitch Tokenization gateway"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.issuerId}
            onChange={value => handleChange("issuerId", value)}
            placeholder="Enter issuer ID"
            inputValidation={validations.issuerId}
            id="tokenReqIssuerId"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Token Requestor ID</span>
            <ToolTip
              toolTip="This is generated by generated by the card scheme"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.tokenRequestorId}
            onChange={value => handleChange("tokenRequestorId", value)}
            placeholder="Enter token requestor ID"
            inputValidation={validations.tokenRequestorId}
            id="tokenReqId"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Card Scheme</span>
            <ToolTip
              toolTip="The card scheme where the token requestor ID was generated"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Select
            value={formData.cardScheme}
            onSelect={value => handleChange("cardScheme", value)}
            options={defaultCardSchemeOptions}
            placeholder="Select Card Scheme"
            theme="light"
            responsive
            dropdownOnTop
            id="tokenReqCardScheme"
            followUp={{
              label: "Use another card scheme",
              onConclude: value => handleChange("cardScheme", value || ""),
              FollowUpModal: NewSchemeModal
            }}
          />
        </div>

        <br />
        <br />
        <div className="flex spaced end vertical-margin">
          <Button
            type="accent"
            buttonType="button"
            id="tokenReqCancel"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button buttonType="submit" loading={loading} id="tokenReqSubmit">
            Submit Configuration
          </Button>
        </div>
      </form>
    </main>
  );
};

const NewSchemeModal: FunctionComponent<ModalProps> = props => {
  const { cancel, visible } = props;
  const [name, setName] = useState("");
  const { notify } = useContext(LayoutContext);

  const saveScheme = () => {
    if (!name) {
      notify("error", "Please enter a card scheme name");
      return;
    }
    if (cancel) {
      cancel({ value: name, label: name });
    }
  };

  useEffect(() => {
    setName("");
  }, [visible]);

  return (
    <Modal {...props}>
      <div className="input-group">
        <span className="question">
          <span className="required">Card scheme name</span>
        </span>
        <Input
          value={name}
          onChange={setName}
          placeholder="Enter card scheme name"
          inputValidation={{ maxLength: 64, transform: alphaNumericValidation }}
        />

        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={cancel ? () => cancel() : undefined}>
            Cancel
          </Button>
          <Button onClick={saveScheme}>Use Card Scheme</Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfigureTokenRequestor;
